/**
 * M30 (2026-05-14) — Sentiment-Regime-Panel.
 *
 * Zeigt AAII Bull-Bear-Spread, CNN Fear/Greed, CBOE Put/Call. Plus
 * berechnetes Contra-Signal wenn 2+ Indikatoren in Extrem-Richtung sind.
 */

import { useCallback } from 'react';
import { useDataState } from '@/hooks/use-data-state';
import { supabase } from '@/lib/supabase';
import { DataStateView } from '@/components/ui/data-state-view';
import { Activity, ChevronDown, ChevronUp } from 'lucide-react';
// W36-U3 — Klartext-Tooltips für AAII / F&G / P/C
import { TermTooltip } from '@/components/ui/term-tooltip';
import { cn } from '@/lib/utils';
import { CardInfoIcon } from '@/components/ui/CardInfoIcon';
import { CARD, CARD_HEADER } from '@/lib/design-tokens';

export interface SentimentRow {
  observation_date: string;
  aaii_bull_pct: number | null;
  aaii_bear_pct: number | null;
  aaii_neutral_pct: number | null;
  aaii_bullbear_spread: number | null;
  fg_index: number | null;
  fg_classification: string | null;
  pc_ratio_equity: number | null;
  pc_ratio_index: number | null;
  regime: string;
  regime_signal: 'bullish_contra' | 'neutral' | 'bearish_contra';
  signals: string[];
  source_url: string | null;
}

/**
 * Audit 08.08.2026 — liefert jetzt die letzte Zeile MIT Angst/Gier-Wert.
 *
 * v5_sentiment_regime wird von zwei Quellen abwechselnd befüllt (eigener
 * Fear&Greed-Composite und AAII-Umfrage); jede schreibt eine eigene Zeile und
 * lässt die Felder der anderen leer. „Neueste Zeile" traf deshalb an
 * AAII-Tagen eine Zeile ohne fg_index — jeder Konsument bekam still `null`
 * für die am stärksten gewichtete Achse, und das Markt-Urteil sprang je
 * nachdem, welche Quelle zuletzt geschrieben hatte.
 *
 * Fällt keine Zeile mit fg_index an, wird auf die absolut neueste Zeile
 * zurückgefallen — sonst verlöre das Panel auch die AAII-Werte.
 */
export async function fetchLatestSentiment(): Promise<SentimentRow | null> {
  const { data, error } = await supabase
    .from('v5_sentiment_regime')
    .select('*')
    .not('fg_index', 'is', null)
    .order('observation_date', { ascending: false })
    .limit(1);
  if (error) throw new Error(error.message);
  if (data?.[0]) return data[0] as SentimentRow;

  const { data: fallback } = await supabase
    .from('v5_sentiment_regime')
    .select('*')
    .order('observation_date', { ascending: false })
    .limit(1);
  return (fallback?.[0] as SentimentRow | undefined) ?? null;
}

const SIGNAL_STYLE = {
  bullish_contra: {
    icon: <ChevronUp className="size-4" />,
    label: 'Antizyklisch bullisch',
    desc: 'Mehrere Indikatoren zeigen extreme Angst — historisch oft Boden, antizyklische Kauf-Chance.',
    className: 'border-emerald-700/60 bg-emerald-950/30 text-emerald-200',
  },
  bearish_contra: {
    icon: <ChevronDown className="size-4" />,
    label: 'Vorsicht — extreme Gier',
    desc: 'Mehrere Indikatoren zeigen extreme Gier — historisch oft Top, antizyklisch defensiv werden.',
    className: 'border-red-700/60 bg-red-950/30 text-red-200',
  },
  neutral: {
    icon: <Activity className="size-4" />,
    label: 'Neutral',
    desc: 'Keine extremen Sentiment-Signale. Markt im normalen Schwankungsbereich.',
    className: 'border-zinc-700/60 bg-zinc-900/30 text-zinc-300',
  },
};

export function SentimentRegimePanel() {
  const queryFn = useCallback(() => fetchLatestSentiment(), []);
  const state = useDataState({
    queryKey: ['sentiment-regime', 'latest'],
    queryFn,
    ttlSeconds: 60 * 60,
  });

  return (
    <section className={CARD}>
      <header className="flex items-center gap-2 mb-2">
        <Activity className="size-4 text-cockpit-accent" />
        <h2 className={CARD_HEADER}>
          Sentiment-Regime
          <CardInfoIcon
            hint={
              <>
                Stimmungs-Indikatoren als <strong>Kontra-Indikator</strong>: Wenn alle euphorisch
                sind (extreme Greed), kommt oft eine Korrektur; wenn alle panisch sind (extreme
                Fear), ist oft schon der Boden gemacht. <strong>AAII</strong> (wöchentliche
                Anlegerumfrage), <strong>Fear/Greed-Index</strong> (CNN, 0-100),{' '}
                <strong>Put/Call-Ratio</strong> (mehr Puts = ängstlich).
              </>
            }
          />
        </h2>
        <span className="text-[10px] text-zinc-500 ml-auto">AAII + F&G + Put/Call · tgl. 22:00 UTC</span>
      </header>
      <DataStateView
        state={state.state}
        empty={
          <div className="rounded-md border border-zinc-800/60 bg-zinc-950/30 p-3 text-[11px] text-zinc-400 leading-snug">
            Noch keine Sentiment-Daten. Cron läuft täglich ~22:00 UTC und holt CNN Fear&Greed + CBOE Put/Call (AAII optional, Mitglieder-Login).
          </div>
        }
      >
        {(s) => {
          if (!s) return null;
          const style = SIGNAL_STYLE[s.regime_signal];
          return (
            <div className="space-y-3">
              <div className={cn('rounded-md border p-3 flex items-start gap-2', style.className)}>
                <span className="shrink-0 mt-0.5">{style.icon}</span>
                <div className="flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="font-semibold text-[13px]">{style.label}</span>
                    <span className="text-[10px] opacity-70">Stand {s.observation_date}</span>
                  </div>
                  <p className="text-[11px] mt-0.5 opacity-90">{style.desc}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <SentimentValue
                  label="AAII Bull-Bear"
                  termKey="aaii-bull-bear"
                  value={s.aaii_bullbear_spread}
                  unit=""
                  hint={
                    s.aaii_bull_pct && s.aaii_bear_pct
                      ? `Bull ${s.aaii_bull_pct.toFixed(1)}% vs Bear ${s.aaii_bear_pct.toFixed(1)}%`
                      : 'Wöchentliche Anlegerumfrage AAII'
                  }
                  warn={
                    s.aaii_bullbear_spread !== null &&
                    Math.abs(s.aaii_bullbear_spread) > 20
                  }
                  danger={
                    s.aaii_bullbear_spread !== null &&
                    Math.abs(s.aaii_bullbear_spread) > 30
                  }
                />
                <SentimentValue
                  label="Fear/Greed"
                  termKey="fear-greed"
                  value={s.fg_index}
                  unit="/100"
                  hint={
                    s.fg_classification
                      ? `${s.fg_classification} (CNN)`
                      : 'CNN Fear & Greed Index'
                  }
                  warn={
                    s.fg_index !== null && (s.fg_index > 70 || s.fg_index < 30)
                  }
                  danger={
                    s.fg_index !== null && (s.fg_index > 75 || s.fg_index < 25)
                  }
                />
                <SentimentValue
                  label="P/C Aktien"
                  termKey="pc-ratio-equity"
                  value={s.pc_ratio_equity}
                  unit=""
                  hint="Equity-Put/Call-Ratio"
                  warn={
                    s.pc_ratio_equity !== null &&
                    (s.pc_ratio_equity < 0.7 || s.pc_ratio_equity > 1.2)
                  }
                  decimals={2}
                />
                <SentimentValue
                  label="P/C Index"
                  termKey="pc-ratio-index"
                  value={s.pc_ratio_index}
                  unit=""
                  hint="Index-Put/Call-Ratio (Hedging-Aktivität)"
                  decimals={2}
                />
              </div>

              {s.signals.length > 0 && (
                <ul className="space-y-1">
                  {s.signals.map((sig, i) => (
                    <li
                      key={i}
                      className="text-[11px] text-zinc-300 leading-snug pl-3 border-l-2 border-zinc-700"
                    >
                      {sig}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        }}
      </DataStateView>
    </section>
  );
}

function SentimentValue({
  label,
  termKey,
  value,
  unit,
  hint,
  warn = false,
  danger = false,
  decimals = 1,
}: {
  label: string;
  /** W36-U3 — TermTooltip-Key fuer ausfuehrliche Erklaerung (meaning/why/for_trade) */
  termKey?: string;
  value: number | null;
  unit?: string;
  hint?: string;
  warn?: boolean;
  danger?: boolean;
  decimals?: number;
}) {
  const valueClass = danger
    ? 'text-red-300'
    : warn
      ? 'text-amber-300'
      : 'text-zinc-200';
  return (
    <div className="rounded-md border border-zinc-800/60 bg-zinc-950/30 p-2" title={hint}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">
        {termKey ? (
          <TermTooltip term={termKey} inline>
            {label}
          </TermTooltip>
        ) : (
          label
        )}
      </div>
      <div className={cn('text-base font-mono', valueClass)}>
        {value !== null
          ? `${value > 0 && label.includes('Bull-Bear') ? '+' : ''}${value.toFixed(decimals)}${unit ?? ''}`
          : '–'}
      </div>
    </div>
  );
}
